import { Component } from '@theme/component';
import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { getBundleStore } from '@theme/pdp-bundle-store';

/**
 * Event consumed by `<ax-product-media-gallery>` to swap the main product
 * image with a watch+band composite. `detail.url` empty/null ⇒ clear.
 */
const BAND_PREVIEW_EVENT = 'product-media:band-preview';

/**
 * @param {string | null | undefined} url
 */
const dispatchBandPreview = (url) => {
  document.dispatchEvent(
    new CustomEvent(BAND_PREVIEW_EVENT, { detail: { url: url || null } })
  );
};

/**
 * @typedef {object} BandSelection
 * @property {string} id - Variant ID (string)
 * @property {string} title - Band product title
 * @property {string} image - Band image URL (small)
 * @property {string} compositeImage - Watch+band composite URL (may be empty)
 * @property {string} price - Pre-formatted money HTML (from Liquid)
 * @property {number} priceCents - Variant price in minor units (cents)
 * @property {string} material - Material slug
 */

/**
 * PDP block that owns the committed band selection. Publishes the selection
 * to the bundle store as the `bands` contributor; the actual /cart/add.json
 * batch is handled by the store at commit time (delegated from
 * `<product-form-component>`).
 *
 * @typedef {object} BlockRefs
 * @property {HTMLUListElement} [list] - UL listing included + selected bands
 * @property {HTMLButtonElement} [cta] - Open-drawer CTA
 * @property {HTMLElement} [ctaAdd] - "Add" label
 * @property {HTMLElement} [ctaEdit] - "Edit selection" label
 *
 * @extends Component<BlockRefs>
 */
class BandUpsellBlock extends Component {
  /** @type {BandSelection[]} */
  #selection = [];

  /** @type {(() => void) | null} */
  #unregisterContributor = null;

  connectedCallback() {
    super.connectedCallback();
    this.#registerWithStore();
    document.addEventListener('model:update', this.#onModelUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#unregisterContributor?.();
    this.#unregisterContributor = null;
    document.removeEventListener('model:update', this.#onModelUpdate);
  }

  #onModelUpdate = () => {
    // productId may differ on the new model — refresh the store's main context.
    const store = getBundleStore();
    store.setContext({
      productId: this.dataset.productId ?? '',
      sectionId: this.dataset.sectionId ?? '',
    });
    // Watch changed → composite is no longer valid; revert to original media.
    dispatchBandPreview(null);
  };

  #registerWithStore() {
    const store = getBundleStore();
    store.setContext({
      productId: this.dataset.productId ?? '',
      sectionId: this.dataset.sectionId ?? '',
    });
    this.#unregisterContributor = store.register({
      name: 'bands',
      order: 10,
      getItems: (ctx) =>
        this.#selection.map((band) => ({
          id: Number(band.id),
          quantity: 1,
          properties: {
            _bundle_role: 'band',
            _bundle_parent_variant: ctx.mainVariantId,
          },
        })),
      getDisplay: () =>
        this.#selection.map((band) => ({
          role: 'band',
          title: band.title,
          priceHtml: band.price,
          priceCents: band.priceCents,
          imageUrl: band.image,
          variantId: band.id,
        })),
      onModelUpdate: () => {
        // Selection is stale after morph (different bands for the new model).
        this.#selection = [];
      },
      onCommit: () => {
        // After a successful add-to-cart batch, clear the visible rows + revert
        // the CTA from "Edit selection" back to "Add bands". Reuses the public
        // commitSelection so #renderList + #syncCta + publish all run.
        this.commitSelection([]);
      },
    });
  }

  /** @returns {BandSelection[]} */
  getSelection() {
    return this.#selection;
  }

  /** @param {BandSelection[]} items */
  commitSelection(items) {
    this.#selection = [...items];
    this.#renderList();
    this.#syncCta();
    getBundleStore().publish('bands');
    this.#publishCompositePreview();
  }

  /**
   * Re-publish the gallery composite preview from the currently committed
   * selection. Called by the drawer after a close-without-validate so the
   * gallery image reverts to whatever was actually committed.
   */
  republishComposite() {
    this.#publishCompositePreview();
  }

  #publishCompositePreview() {
    const last = this.#selection[this.#selection.length - 1];
    dispatchBandPreview(last?.compositeImage);
  }

  openDrawer() {
    const drawer = document.getElementById(this.dataset.drawerId ?? '');
    if (drawer && 'showDialog' in drawer && typeof drawer.showDialog === 'function') {
      drawer.showDialog();
    }
  }

  /** @param {string} variantId */
  removeBand(variantId) {
    this.#selection = this.#selection.filter((band) => band.id !== variantId);
    this.#renderList();
    this.#syncCta();
    getBundleStore().publish('bands');
    this.#publishCompositePreview();
  }

  #renderList() {
    const { list } = this.refs;
    if (!list) return;

    /** @type {HTMLElement[]} */
    const currentRows = Array.from(list.querySelectorAll('.band-upsell-block__row--selected'));
    const currentById = new Map(currentRows.map((row) => [row.dataset.variantId ?? '', row]));
    const nextIds = new Set(this.#selection.map((b) => b.id));

    for (const row of currentRows) {
      const id = row.dataset.variantId ?? '';
      if (!nextIds.has(id)) this.#animateOutAndRemove(row);
    }

    for (const band of this.#selection) {
      if (currentById.has(band.id)) continue;
      const row = this.#buildRow(band);
      row.classList.add('is-adding');
      list.appendChild(row);
      if (!prefersReducedMotion()) {
        onAnimationEnd(row, () => row.classList.remove('is-adding'));
      } else {
        row.classList.remove('is-adding');
      }
    }
  }

  /** @param {HTMLElement} row */
  #animateOutAndRemove(row) {
    const remove = () => row.remove();
    if (prefersReducedMotion()) return remove();
    row.style.setProperty('--row-height', `${row.clientHeight}px`);
    row.classList.add('is-removing');
    onAnimationEnd(row, remove);
  }

  /**
   * @param {BandSelection} band
   * @returns {HTMLLIElement}
   */
  #buildRow(band) {
    const li = document.createElement('li');
    li.className = 'band-upsell-block__row band-upsell-block__row--selected';
    li.dataset.variantId = band.id;

    const thumb = document.createElement('span');
    thumb.className = 'band-upsell-block__thumb';
    if (band.image) {
      const img = document.createElement('img');
      img.src = band.image;
      img.className = 'band-upsell-block__image';
      img.loading = 'lazy';
      img.alt = band.title;
      img.width = 60;
      img.height = 60;
      thumb.appendChild(img);
    }
    li.appendChild(thumb);

    const label = document.createElement('span');
    label.className = 'band-upsell-block__label';
    label.textContent = band.title;
    li.appendChild(label);

    const price = document.createElement('span');
    price.className = 'band-upsell-block__price';
    price.innerHTML = band.price;
    li.appendChild(price);

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'band-upsell-block__remove';
    remove.dataset.variantId = band.id;
    remove.setAttribute('aria-label', `${this.#removeAriaPrefix()}${band.title}`);
    remove.innerHTML = '<span aria-hidden="true">×</span>';
    remove.addEventListener('click', this.#handleRemoveClick);
    li.appendChild(remove);

    return li;
  }

  /** @returns {string} */
  #removeAriaPrefix() {
    return document.documentElement.lang?.startsWith('fr') ? 'Retirer ' : 'Remove ';
  }

  /** @param {MouseEvent} event */
  #handleRemoveClick = (event) => {
    const target = event.currentTarget;
    if (!(target instanceof HTMLElement)) return;
    const id = target.dataset.variantId;
    if (id) this.removeBand(id);
  };

  #syncCta() {
    const hasSelection = this.#selection.length > 0;
    this.toggleAttribute('data-has-selection', hasSelection);
    if (this.refs.ctaAdd) this.refs.ctaAdd.hidden = hasSelection;
    if (this.refs.ctaEdit) this.refs.ctaEdit.hidden = !hasSelection;
  }

}

if (!customElements.get('band-upsell-block')) {
  customElements.define('band-upsell-block', BandUpsellBlock);
}

/**
 * Drawer extending DialogComponent — multi-select of bands with filter pills.
 *
 * @typedef {object} DrawerRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLUListElement} [grid]
 * @property {HTMLElement} [chips]
 * @property {HTMLButtonElement} [validateButton]
 * @property {HTMLElement} [validateLabel]
 * @property {HTMLElement} [validateCount]
 * @property {HTMLInputElement[]} [bandInputs]
 *
 * @extends DialogComponent
 */
class BandUpsellDrawer extends DialogComponent {
  /** @type {Set<string>} */
  #workingSelection = new Set();

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(DialogOpenEvent.eventName, this.#handleOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleClose);
  }

  /** @returns {HTMLElement | undefined} */
  #grid() {
    return /** @type {HTMLElement | undefined} */ (this.refs.grid);
  }

  /** @returns {HTMLInputElement[]} */
  #bandInputs() {
    return /** @type {HTMLInputElement[]} */ (this.refs.bandInputs ?? []);
  }

  #handleOpen = () => {
    const grid = this.#grid();
    if (grid) {
      document.dispatchEvent(
        new CustomEvent('removeLazyKeep', {
          detail: { lazyParentElement: grid },
        })
      );
    }

    const committed = this.#getBlock()?.getSelection() ?? [];
    this.#workingSelection = new Set(committed.map((b) => b.id));

    for (const input of this.#bandInputs()) {
      if (input.disabled) continue;
      input.checked = this.#workingSelection.has(input.value);
    }

    this.#syncChips();
    this.#updateValidateButton();
    this.#publishWorkingPreview();
  };

  /**
   * Drawer was just closed. If validate was used, the block already published
   * the new committed composite via commitSelection. If the user closed without
   * validating, we ask the block to re-publish its committed selection so the
   * gallery reverts to whatever is actually in the cart-ready state.
   */
  #handleClose = () => {
    this.#getBlock()?.republishComposite();
  };

  /**
   * Push the gallery preview that matches the current working selection
   * (last-added wins). Empty working set ⇒ clear (gallery reverts).
   */
  #publishWorkingPreview() {
    const ids = [...this.#workingSelection];
    const lastId = ids[ids.length - 1];
    if (!lastId) {
      dispatchBandPreview(null);
      return;
    }
    const input = this.#bandInputs().find((i) => i.value === lastId);
    dispatchBandPreview(input?.dataset.bandCompositeImage);
  }

  /** @param {string} material */
  setFilter(material) {
    const grid = this.#grid();
    if (!grid) return;
    if (grid.dataset.activeFilter === material) return;

    const items = /** @type {HTMLElement[]} */ (
      Array.from(grid.querySelectorAll('.band-upsell-drawer__item'))
    );

    // FLIP — capture positions before the filter change so the remaining items
    // can slide smoothly into their new spots once the hidden ones are removed
    // from layout. Using a scoped, DOM-local animation avoids the top-layer
    // stacking regression caused by document-level view transitions.
    const firstRects = items.map((item) => item.getBoundingClientRect());

    grid.dataset.activeFilter = material;

    for (const item of items) {
      item.hidden = material !== 'all' && item.dataset.material !== material;
    }

    const pills = this.querySelectorAll('.band-upsell-drawer__pill');
    for (const pill of pills) {
      if (!(pill instanceof HTMLElement)) continue;
      const isActive = pill.dataset.filter === material;
      pill.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      pill.classList.toggle('is-active', isActive);
    }

    if (prefersReducedMotion()) return;

    // Soft deceleration curve (easeOutQuint). Items that slide use the full
    // duration; the fade-out on leaving items runs slightly shorter so they
    // clear visually before the incoming slide settles.
    const slideDuration = 380;
    const fadeDuration = 280;
    const easing = 'cubic-bezier(0.22, 1, 0.36, 1)';

    for (const [i, item] of items.entries()) {
      const first = firstRects[i];
      if (!first) continue;
      const last = item.getBoundingClientRect();

      const wasHidden = first.width === 0 && first.height === 0;
      const isHidden = last.width === 0 && last.height === 0;

      if (isHidden) {
        // Hidden by the filter — fade a detached clone at the old slot so the
        // remaining items can reflow immediately while this one animates out.
        if (!wasHidden) this.#animateLeavingItem(item, first, fadeDuration, easing);
      } else if (wasHidden) {
        // Newly revealed — slide in from slightly above with a fade.
        item.animate(
          [
            { opacity: 0, transform: 'translateY(-12px)' },
            { opacity: 1, transform: 'none' },
          ],
          { duration: slideDuration, easing }
        );
      } else {
        const dx = first.left - last.left;
        const dy = first.top - last.top;
        if (dx === 0 && dy === 0) continue;
        item.animate(
          [
            { transform: `translate(${dx}px, ${dy}px)` },
            { transform: 'none' },
          ],
          { duration: slideDuration, easing }
        );
      }
    }
  }

  /**
   * @param {HTMLElement} item
   * @param {DOMRect} first
   * @param {number} duration
   * @param {string} easing
   */
  #animateLeavingItem(item, first, duration, easing) {
    const parent = item.parentElement;
    if (!parent) return;

    const ghost = /** @type {HTMLElement} */ (item.cloneNode(true));
    // Prevent the clone from interfering with forms/inputs state.
    ghost.querySelectorAll('input, button').forEach((el) => {
      if (el instanceof HTMLElement) el.setAttribute('tabindex', '-1');
    });
    Object.assign(ghost.style, {
      position: 'fixed',
      top: `${first.top}px`,
      left: `${first.left}px`,
      width: `${first.width}px`,
      height: `${first.height}px`,
      margin: '0',
      pointerEvents: 'none',
      zIndex: '0',
    });
    ghost.setAttribute('aria-hidden', 'true');
    parent.appendChild(ghost);

    const anim = ghost.animate(
      [
        { opacity: 1, transform: 'scale(1)' },
        { opacity: 0, transform: 'scale(0.96)' },
      ],
      { duration, easing }
    );
    anim.finished.catch(() => {}).finally(() => ghost.remove());
  }

  /** @param {Event} event */
  handleSelect(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    if (input.checked) {
      this.#workingSelection.add(input.value);
    } else {
      this.#workingSelection.delete(input.value);
    }
    this.#syncChips();
    this.#updateValidateButton();
    this.#publishWorkingPreview();
  }

  /** @param {string} variantId */
  removeChip(variantId) {
    this.#workingSelection.delete(variantId);
    const input = this.#bandInputs().find((i) => i.value === variantId);
    if (input) input.checked = false;
    this.#syncChips();
    this.#updateValidateButton();
    this.#publishWorkingPreview();
  }

  validate() {
    // Iterate the working selection (insertion-ordered Set), not the DOM, so
    // the committed array preserves the user's check order — last item is the
    // most recently selected band, which is what drives the gallery composite.
    const inputsByValue = new Map(this.#bandInputs().map((i) => [i.value, i]));
    /** @type {BandSelection[]} */
    const items = [];
    for (const id of this.#workingSelection) {
      const input = inputsByValue.get(id);
      if (!input || !input.checked) continue;
      items.push({
        id: input.value,
        title: input.dataset.bandTitle ?? '',
        image: input.dataset.bandImage ?? '',
        compositeImage: input.dataset.bandCompositeImage ?? '',
        price: input.dataset.bandPrice ?? '',
        priceCents: Number(input.dataset.bandPriceCents) || 0,
        material: input.dataset.bandMaterial ?? '',
      });
    }
    this.#getBlock()?.commitSelection(items);
    this.closeDialog();
  }

  #syncChips() {
    const chips = /** @type {HTMLElement | undefined} */ (this.refs.chips);
    if (!chips) return;

    const checkedById = new Map(
      this.#bandInputs().filter((i) => i.checked).map((i) => [i.value, i])
    );

    /** @type {HTMLElement[]} */
    const currentChips = Array.from(chips.querySelectorAll('.band-upsell-drawer__chip'));
    const currentIds = new Set(currentChips.map((c) => c.dataset.variantId ?? ''));

    for (const chip of currentChips) {
      const id = chip.dataset.variantId ?? '';
      if (!checkedById.has(id)) this.#animateOutChip(chip);
    }

    for (const [id, input] of checkedById) {
      if (currentIds.has(id)) continue;
      const chip = this.#buildChip(input);
      chip.classList.add('is-adding');
      chips.appendChild(chip);
      if (!prefersReducedMotion()) {
        onAnimationEnd(chip, () => chip.classList.remove('is-adding'));
      } else {
        chip.classList.remove('is-adding');
      }
    }
  }

  /**
   * @param {HTMLInputElement} input
   * @returns {HTMLButtonElement}
   */
  #buildChip(input) {
    const chip = document.createElement('button');
    chip.type = 'button';
    chip.className = 'band-upsell-drawer__chip';
    chip.dataset.variantId = input.value;
    chip.setAttribute('aria-label', `${this.#removeAriaPrefix()}${input.dataset.bandTitle ?? ''}`);

    const label = document.createElement('span');
    label.className = 'band-upsell-drawer__chip-label';
    label.textContent = input.dataset.bandTitle ?? '';
    chip.appendChild(label);

    const price = document.createElement('span');
    price.className = 'band-upsell-drawer__chip-price';
    price.innerHTML = input.dataset.bandPrice ?? '';
    chip.appendChild(price);

    const close = document.createElement('span');
    close.className = 'band-upsell-drawer__chip-close';
    close.setAttribute('aria-hidden', 'true');
    close.textContent = '×';
    chip.appendChild(close);

    chip.addEventListener('click', () => this.removeChip(input.value));
    return chip;
  }

  /** @param {HTMLElement} chip */
  #animateOutChip(chip) {
    const remove = () => chip.remove();
    if (prefersReducedMotion()) return remove();
    chip.classList.add('is-removing');
    onAnimationEnd(chip, remove);
  }

  #updateValidateButton() {
    const count = this.#workingSelection.size;
    const validateButton = /** @type {HTMLButtonElement | undefined} */ (this.refs.validateButton);
    const validateCount = /** @type {HTMLElement | undefined} */ (this.refs.validateCount);
    if (validateButton) {
      validateButton.hidden = count === 0;
      validateButton.disabled = count === 0;
    }
    if (validateCount) {
      if (count > 0) {
        validateCount.hidden = false;
        validateCount.textContent = String(count);
      } else {
        validateCount.hidden = true;
        validateCount.textContent = '';
      }
    }
  }

  #removeAriaPrefix() {
    return document.documentElement.lang?.startsWith('fr') ? 'Retirer ' : 'Remove ';
  }

  /** @returns {BandUpsellBlock | null} */
  #getBlock() {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return null;
    const block = document.getElementById(`BandUpsellBlock-${sectionId}`);
    return block instanceof BandUpsellBlock ? block : null;
  }
}

if (!customElements.get('band-upsell-drawer')) {
  customElements.define('band-upsell-drawer', BandUpsellDrawer);
}
